import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Clock, Check, FileText, House } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { buildRoute } from '@/config/routes';
import { AddToCartButton } from '@/components/orders/AddToCartButton';
import {
  STATUS_TONE_CLASSES,
  statusToTone,
  type StatusTone,
  type PropertyTable
} from '../metadata/propertyConfig';

type Props = {
  item: PropertyTable;
  isSelected: boolean;
  onSelectChange: (checked: boolean) => void;
  onAddToCart: (item: PropertyTable) => void;
  onOpen?: (item: PropertyTable) => void; // optional; required-by-design once mounted (Task 15 wires it)
  isAddingToCart?: boolean;
  /** True when this property is already in the cart. */
  isInCart?: boolean;
  /**
   * Whether add-to-cart affordances render. Defaults true. When false (a role
   * the cart route rejects, DV-2751) both the selection checkbox and the
   * footer Add-to-Cart button collapse together — no dead checkbox, no blank
   * padded strip.
   */
  canAddToCart?: boolean;
};

type RawPropertyApiResponse = {
  logoUrl?: string | null;
  orderId?: string | null;
  orderNumber?: string | null;
  orderStatus?: string | null;
  lastOrderDate?: number | null;
};

const formatLastOrderDate = (epochSeconds: number | null | undefined) =>
  epochSeconds
    ? dayjs(new Date(epochSeconds * 1000)).format('MMM DD, YYYY')
    : '-';

type OrderStrip = {
  icon: React.ReactNode;
  label: string;
  date: string | null;
  orderNumber: string | null;
  tone: StatusTone;
};

const buildOrderStrip = (
  raw: RawPropertyApiResponse,
  lastOrderDate: number | null | undefined,
  orderNumberFromTable: string | null
): OrderStrip => {
  const isActive =
    !!raw?.orderId && !!raw?.orderStatus && raw.orderStatus !== 'Completed';
  if (isActive) {
    return {
      icon: <Clock className="h-3.5 w-3.5" />,
      label: (raw.orderStatus as string).toUpperCase(),
      date: formatLastOrderDate(lastOrderDate),
      orderNumber: orderNumberFromTable,
      tone: statusToTone(raw.orderStatus as string)
    };
  }
  if (lastOrderDate) {
    return {
      icon: <Check className="h-3.5 w-3.5" />,
      label: 'LAST ORDER',
      date: formatLastOrderDate(lastOrderDate),
      orderNumber: orderNumberFromTable,
      tone: 'neutral'
    };
  }
  return {
    icon: <FileText className="h-3.5 w-3.5" />,
    label: 'NO ORDERS',
    date: null,
    orderNumber: null,
    tone: 'neutral'
  };
};

export const PropertyCard = ({
  item,
  isSelected,
  onSelectChange,
  onAddToCart,
  onOpen,
  isAddingToCart = false,
  isInCart = false,
  canAddToCart = true,
}: Props) => {
  const navigate = useNavigate();
  const raw = (item.apiResponseRaw ?? {}) as RawPropertyApiResponse;
  const hasPhotoset = item.hasPhotoset === true;
  const formattedAddress = item.formattedAddress ?? '';
  // services/property-address.getPropertyAddress builds formattedAddress
  // as "address1\n[address2,unit]\ncity, state zip" — split here so the
  // card can render it as a letter (line 1 emphasised, lines 2+ muted).
  const [addressLine1, ...addressRest] = formattedAddress
    .split('\n')
    .filter(Boolean);
  const orderNumberFromTable =
    (item.orderNumber as { data?: string | null } | undefined)?.data ?? null;
  const lastOrderDate =
    (item.lastOrderDate as { data?: number | null } | undefined)?.data ??
    raw.lastOrderDate ??
    null;
  const order = buildOrderStrip(raw, lastOrderDate, orderNumberFromTable);
  const subtitleText =
    (item.portfolioName as string | null) || (item.entity as string | null);

  return (
    <Card
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={
        onOpen ? `Open details for ${item.formattedAddress}` : undefined
      }
      onClick={onOpen ? () => onOpen(item) : undefined}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(item);
              }
            }
          : undefined
      }
      className={cn(
        'flex flex-col h-full overflow-hidden transition-shadow hover:shadow-lg border-border',
        onOpen &&
          'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25',
        isSelected && 'ring ring-primary border-0'
      )}
      data-testid="property-card"
    >
      <div className="relative aspect-[3/2] bg-surface-inset">
        {raw.logoUrl ? (
          <img
            src={raw.logoUrl}
            alt={item.formattedAddress ?? ''}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center bg-surface-inset"
            aria-label="No Property Image"
          >
            <House
              className="h-16 w-16 text-muted-foreground"
              strokeWidth={1.5}
            />
          </div>
        )}
        {canAddToCart && (
          <div className="absolute top-2 left-2">
            <Checkbox
              checked={isSelected}
              onCheckedChange={(c) => onSelectChange(c === true)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Select ${item.formattedAddress}`}
              boxClassName="bg-background/90 shadow-sm"
            />
          </div>
        )}
        {hasPhotoset && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(buildRoute.propertyAssets(item.id));
            }}
            className="absolute bottom-2 right-2 inline-flex items-center rounded-full border border-border bg-background/95 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-foreground shadow-sm hover:bg-background hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 max-lg:min-h-11 max-lg:px-4"
          >
            View assets
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-3">
        <div>
          {addressLine1 && (
            <Typography
              as="p"
              className="text-base font-semibold leading-6 text-foreground line-clamp-2"
            >
              {addressLine1}
            </Typography>
          )}
          {addressRest.map((line) => (
            <Typography
              key={line}
              as="p"
              className="text-sm leading-5 text-muted-foreground line-clamp-1"
            >
              {line}
            </Typography>
          ))}
        </div>
        <Typography
          as="p"
          className={cn(
            'text-sm leading-5 line-clamp-1',
            subtitleText ? 'text-muted-foreground' : 'invisible'
          )}
        >
          {subtitleText || '·'}
        </Typography>
      </div>

      <div
        className={cn(
          'mx-3 mb-3 flex h-10 items-center gap-2 rounded-md border px-3',
          STATUS_TONE_CLASSES[order.tone]
        )}
      >
        {order.icon}
        <Typography
          as="p"
          variant="small-semibold"
          className="flex-1 min-w-0 truncate uppercase tracking-wider"
        >
          {order.label}
          {order.date && ` · ${order.date}`}
        </Typography>
      </div>

      {canAddToCart && (
        <div className="px-3 pb-3">
          <AddToCartButton
            isAdding={isAddingToCart}
            isAdded={isInCart}
            idleVariant="primaryOutline"
            onAddToCart={(e) => {
              e.stopPropagation();
              onAddToCart(item);
            }}
            className="w-full"
          />
        </div>
      )}
    </Card>
  );
};
